import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import Navigation from "@/components/Navigation";
import TopHeader from "@/components/TopHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import axios from "axios";
import { Users, Heart, Sparkles, Baby, Check, Plus, MessageCircle, ArrowRight } from "lucide-react";

const Circles = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [circles, setCircles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(null);

  useEffect(() => {
    fetchCircles();
  }, [filter]);

  const fetchCircles = async () => {
    setLoading(true);
    try {
      const params = filter ? `?phase=${filter}` : '';
      const response = await axios.get(`${API}/circles${params}`, { withCredentials: true });
      setCircles(response.data);
    } catch (error) {
      console.error("Fetch circles error:", error);
      toast.error("Failed to load circles");
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async (circleId) => {
    try {
      await axios.post(`${API}/circles/${circleId}/join`, {}, { withCredentials: true });
      toast.success("Joined the circle!");
      fetchCircles();
    } catch (error) {
      if (error.response?.data?.detail === "Already a member") {
        toast.info("You're already a member");
      } else {
        toast.error("Failed to join circle");
      }
    }
  };

  const handleLeave = async (circleId) => {
    try {
      await axios.post(`${API}/circles/${circleId}/leave`, {}, { withCredentials: true });
      toast.success("Left the circle");
      fetchCircles();
    } catch (error) {
      toast.error("Failed to leave circle");
    }
  };

  const getPhaseIcon = (phase) => {
    switch (phase) {
      case 'ttc': return <Sparkles className="w-4 h-4" />;
      case 'pregnant': return <Heart className="w-4 h-4" />;
      case 'postpartum': return <Baby className="w-4 h-4" />;
      default: return <Users className="w-4 h-4" />;
    }
  };

  const getPhaseLabel = (phase) => {
    switch (phase) {
      case 'ttc': return 'Trying to Conceive';
      case 'pregnant': return 'Pregnant';
      case 'postpartum': return 'Postpartum';
      case 'all': return 'All Phases';
      default: return phase;
    }
  };

  return (
    <div className="min-h-screen bg-cream pb-24">
      <Navigation />
      <TopHeader title="Circles" />

      {/* Filter */}
      <div className="px-6 mt-4 mb-6">
        <div className="max-w-4xl mx-auto flex gap-2 overflow-x-auto pb-2">
          <Button 
            variant={filter === null ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter(null)}
            className={`rounded-full ${filter === null ? 'bg-sage text-white' : 'border-sage/30 text-sage-darker'}`}
            data-testid="filter-all"
          >
            All Circles
          </Button>
          <Button 
            variant={filter === 'ttc' ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter('ttc')}
            className={`rounded-full ${filter === 'ttc' ? 'bg-clay text-white' : 'border-sage/30 text-sage-darker'}`}
            data-testid="filter-ttc"
          >
            <Sparkles className="w-4 h-4 mr-1" />
            TTC
          </Button>
          <Button 
            variant={filter === 'pregnant' ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter('pregnant')}
            className={`rounded-full ${filter === 'pregnant' ? 'bg-sage text-white' : 'border-sage/30 text-sage-darker'}`}
            data-testid="filter-pregnant"
          >
            <Heart className="w-4 h-4 mr-1" />
            Pregnant
          </Button>
          <Button 
            variant={filter === 'postpartum' ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter('postpartum')}
            className={`rounded-full ${filter === 'postpartum' ? 'bg-purple-500 text-white' : 'border-sage/30 text-sage-darker'}`}
            data-testid="filter-postpartum"
          >
            <Baby className="w-4 h-4 mr-1" />
            Postpartum
          </Button>
        </div>
      </div>

      {/* Circles Grid */}
      <main className="px-6" data-testid="circles-main">
        <div className="max-w-4xl mx-auto">
          {loading ? (
            <div className="grid md:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <Card key={i} className="bg-white border-sage/20 animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-32 bg-sage/10 rounded-xl mb-4" />
                    <div className="h-6 bg-sage/10 rounded w-3/4 mb-2" />
                    <div className="h-4 bg-sage/10 rounded w-1/2" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : circles.length === 0 ? (
            <div className="text-center py-16">
              <Users className="w-16 h-16 text-sage/40 mx-auto mb-4" />
              <h3 className="text-xl font-serif text-sage-darker mb-2">No circles found</h3>
              <p className="text-muted-foreground">
                {filter ? `No ${getPhaseLabel(filter)} circles available` : "Check back later for new circles"}
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {circles.map((circle) => (
                <Card 
                  key={circle.circle_id} 
                  className="bg-white border-sage/20 hover:shadow-lg transition-shadow overflow-hidden"
                  data-testid={`circle-${circle.circle_id}`}
                >
                  <CardContent className="p-0">
                    {/* Circle Image */}
                    <div className="relative h-40">
                      <img 
                        src={circle.image_url || "https://images.unsplash.com/photo-1581900016271-bab836b53546?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"}
                        alt={circle.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-white to-transparent" />
                      <Badge className={`absolute top-4 left-4 phase-${circle.phase}`}>
                        {getPhaseIcon(circle.phase)}
                        <span className="ml-1">{getPhaseLabel(circle.phase)}</span>
                      </Badge>
                    </div>
                    
                    {/* Circle Info */}
                    <div className="p-5 space-y-4">
                      <div>
                        <h3 className="text-lg font-serif text-sage-darker mb-1">{circle.name}</h3>
                        <p className="text-muted-foreground text-sm line-clamp-2">{circle.description}</p>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-sage" />
                          <span className="text-sm text-muted-foreground">{circle.member_count} members</span>
                        </div>
                        
                        {circle.is_member ? (
                          <div className="flex gap-2">
                            <Button 
                              size="sm"
                              onClick={() => navigate(`/circles/${circle.circle_id}`)}
                              className="bg-sage text-white hover:bg-sage-dark rounded-full"
                              data-testid={`enter-circle-${circle.circle_id}`}
                            >
                              <MessageCircle className="w-4 h-4 mr-1" />
                              Enter
                            </Button>
                            <Button 
                              variant="outline"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleLeave(circle.circle_id); }}
                              className="border-sage/30 text-sage-dark hover:bg-sage/10 rounded-full"
                              data-testid={`leave-circle-${circle.circle_id}`}
                            >
                              Leave
                            </Button>
                          </div>
                        ) : (
                          <Button 
                            size="sm"
                            onClick={() => handleJoin(circle.circle_id)}
                            className="bg-sage text-white hover:bg-sage-dark rounded-full"
                            data-testid={`join-circle-${circle.circle_id}`}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Join
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Circles;
